import { Theme, TranslatableTypography } from "common";
import DownloadLink from "../shared/DownloadLink";
import DownloadFailedLink from "../shared/DownloadFailedLink";
import StatusCell from "./StatusCell";

/**
 * History Table MUI Column configuration
 *
 * @see https://mui.com/x/react-data-grid/column-definition/
 */
export const getTableColumnHeaders = ({
  t,
  getStatusIcon,
  getRecords,
  handleOpenRecentActivityDialog,
  downloadFile,
}) => {
  const tableColumns = [
    {
      field: "fileName",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.fileName" />
      ),
      renderCell: (params) => (
        <DownloadLink
          href={params.row.links[1].href}
          filename={params.row.filename}
          downloadFile={downloadFile}
        />
      ),
      sortable: false,
      width: 400,
    },
    {
      field: "source",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.source" />
      ),
      renderCell: ({ row }) => t(`importSource.${row.source}`),
      sortable: false,
      width: 120,
    },
    {
      field: "type",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.type" />
      ),
      renderCell: ({ row }) => t(`importTypes.${row.type}`),
      sortable: false,
      width: 160,
    },
    {
      field: "fileType",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.fileType" />
      ),
      renderCell: ({ row }) => t(`fileTypes.${row.contentType}`),
      sortable: false,
      width: 110,
    },
    {
      field: "environment",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.environment" />
      ),
      renderCell: ({ row }) => t(`oncoreInstances.${row.oncoreInstance}`),
      sortable: false,
      width: 135,
    },
    {
      field: "status",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.status" />
      ),
      renderCell: ({ row }) => (
        <StatusCell
          row={row}
          getStatusIcon={getStatusIcon}
          handleOpenRecentActivityDialog={handleOpenRecentActivityDialog}
        />
      ),
      sortable: false,
      width: 170,
    },
    {
      field: "records",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.records" />
      ),
      renderCell: ({ row }) => getRecords(row),
      sortable: false,
      width: 100,
    },
    {
      field: "failed",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.failed" />
      ),
      renderCell: ({ row }) => (
        <DownloadFailedLink row={row} downloadFile={downloadFile} />
      ),
      sortable: false,
      width: 90,
    },
    {
      field: "uploadedBy",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.uploadedBy" />
      ),
      sortable: false,
      width: 136,
    },
    {
      field: "uploadedDate",
      renderHeader: () => (
        <TranslatableTypography tlEntry="ois.table.columnName.uploadedDate" />
      ),
      renderCell: ({ row }) => new Date(row.uploadedDate).toLocaleString(),
      sortable: false,
      width: 160,
    },
  ];

  // MUI thought it'd be a great idea to deprecate the 'hide' property.
  // The new columnVisibilityModel is overly complex for what we need &
  // more geared to user-interaction to show/hide a column:
  // @see https://mui.com/x/react-data-grid/column-visibility/#initialize-the-visible-columns
  return tableColumns.filter((c) => !c.hide);
};

export const tableStyles = {
  boxShadow: 3,
  borderRadius: "8px",
  width: "100%",
  "& .MuiDataGrid-virtualScroller": {
    overflow: "hidden",
  },
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitle": {
      fontSize: "14px",
      fontWeight: 400,
    },
    "&:focus": {
      outline: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
    },
  },
  "& .MuiDataGrid-columnHeader--last .MuiDataGrid-columnSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-columnSeparator": {
    visibility: "visible",
  },
  "& .MuiDataGrid-cell": {
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    "&:focus": {
      outline: "none !important",
    },
    "&:focus-within": {
      outline: "none !important",
    },
  },
  "& .MuiTablePagination-root": {
    "& .MuiTablePagination-selectLabel": {
      fontSize: "14px",
    },
    "& .MuiTablePagination-displayedRows": {
      fontSize: "14px",
    },
    fontSize: "14px",
  },
  "&& .MuiIconButton-root": {
    width: "1em",
    height: "1em",
  },
  "& .Mui-odd": {
    backgroundColor: "#F0F4F5",
    "&:hover": {
      backgroundColor: "#F0F4F5",
    },
  },
  "& .Mui-even": {
    "&:hover": {
      backgroundColor: Theme.palette.primary.contrastText,
    },
  },
  "& .MuiCheckbox-root": {
    padding: "0px",
  },
  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
    minHeight: "52px !important",
    padding: "5px 10px",
  },
  "& .MuiDataGrid-columnHeaderCheckbox": {
    minWidth: "30px !important",
    width: "30px !important",
    "& .MuiDataGrid-columnSeparator": {
      display: "none",
    },
  },
  "& .MuiDataGrid-cellCheckbox": {
    minWidth: "30px !important",
    width: "30px !important",
    padding: "0px !important",
  },
  "& .MuiDataGrid-scrollbar--horizontal ": {
    display: "none",
  },
  "& .MuiDataGrid-filler": {
    display: "none",
  },
};
